import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/context/ClientAuth";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function AuthShell({ title, tag, children, footer }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 grain relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-sm w-full relative z-10"
      >
        <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em]">{tag}</span>
        <h1 className="font-display font-bold uppercase tracking-tighter text-4xl mt-3 text-white leading-[0.9]">{title}</h1>
        <div className="mt-8 space-y-5">{children}</div>
        <div className="mt-6 text-center text-white/60 text-xs uppercase tracking-[0.15em]">{footer}</div>
        <Link to="/" className="block mt-4 text-center text-white/50 text-xs uppercase tracking-[0.2em] link-underline">Back to site</Link>
      </motion.div>
    </div>
  );
}

export const field =
  "w-full bg-transparent border-b border-white/20 py-3 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/45 text-white";
export const goldBtn =
  "mt-2 w-full bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-40";

export default function Login() {
  const { login } = useClientAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell tag="Members" title="Sign in" footer={<>No account? <Link to="/signup" className="text-[#D4AF37]">Sign up</Link></>}>
      <input data-testid="login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
      <input data-testid="login-password" type="password" placeholder="Password" value={password}
        onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} className={field} />
      <button data-testid="login-submit" onClick={submit} disabled={busy || !email || !password} className={goldBtn}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
      </button>
      <div className="text-center">
        <Link to="/forgot" data-testid="forgot-link" className="text-white/60 text-xs uppercase tracking-[0.15em] link-underline">Forgot password?</Link>
      </div>
    </AuthShell>
  );
}
